import { memo, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import { cast } from '~/utils/helper';
import { CloseIcon, MarkerIcon } from '~/assets/icons';
import Map, { Layer, MapRef, Marker, Source } from 'react-map-gl/mapbox';
import { ILocation } from '~/utils/interface/location';
import BaseRow from '~/components/common/base-row/BaseRow';
import dayjs from 'dayjs';
import { SHAP_TYPES } from '~/utils/constants/common';
import mapboxgl from 'mapbox-gl';
import * as turf from '@turf/turf';

type DetailLocationDialogProps = {
  data?: ILocation;
  open: boolean;
  setOpen?: (open: boolean, needRefreshData?: boolean) => void;
};

const DetailLocationDialog = (props: DetailLocationDialogProps) => {
  const { t } = useTranslation('admin/location');

  const [mapLoaded, setMapLoaded] = useState<boolean>(false);
  const [marker, setMarker] = useState<{ lng: number; lat: number } | null>(null);
  const [circleRadius, setCircleRadius] = useState<number | undefined>(undefined);

  const mapRef = useRef<MapRef | null>(null);

  useEffect(() => {
    if (props.open && props.data) {
      (async () => {
        const data = props.data!;
        const points = data.points || [];

        if (!points.length) return;

        const first = points[0];
        setMarker(first);

        if (data.type === SHAP_TYPES.circle && data.radius) {
          setCircleRadius(Number(data.radius));
        }

        const map = mapRef.current?.getMap();
        if (!map) return;

        if (points.length === 1) {
          map.flyTo({
            center: [first.lng, first.lat],
            zoom: 13,
          });
        } else {
          const bounds = new mapboxgl.LngLatBounds();
          points.forEach((p) => bounds.extend([p.lng, p.lat]));
          map.fitBounds(bounds, { padding: 80, duration: 1000 });
        }
      })();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open, props.data, mapLoaded]);

  const renderPointRadius = () => {
    const centerPoint = props.data?.points?.[0] ?? marker;

    if (props.data?.type === SHAP_TYPES.circle && centerPoint && circleRadius) {
      const radiusInKm = circleRadius / 1000;

      const circleGeoJson = turf.circle([centerPoint.lng, centerPoint.lat], radiusInKm, {
        steps: 64,
        units: 'kilometers',
      });

      return (
        <Source id='circle-source' type='geojson' data={circleGeoJson as any}>
          <Layer
            id='circle-fill'
            type='fill'
            paint={{
              'fill-color': 'rgba(66, 135, 245, 0.3)',
            }}
          />

          <Layer
            id='circle-outline'
            type='line'
            paint={{
              'line-color': 'rgba(66, 135, 245, 0.8)',
              'line-width': 2,
            }}
          />
        </Source>
      );
    }

    return null;
  };

  const renderPolygon = () => {
    if (props.data?.type !== SHAP_TYPES.polygon) return null;

    const points = props.data?.points || [];

    const coords = points
      .filter((p): p is { lng: number; lat: number } => typeof p.lng === 'number' && typeof p.lat === 'number')
      .map((p) => [p.lng, p.lat]);

    if (coords.length < 2) return null;

    const polygon: any =
      coords.length >= 3
        ? {
            type: 'Feature',
            geometry: {
              type: 'Polygon',
              coordinates: [[...coords, coords[0]]],
            },
            properties: {},
          }
        : null;

    const lineString: any = {
      type: 'Feature',
      geometry: {
        type: 'LineString',
        coordinates: coords,
      },
      properties: {},
    };

    return (
      <>
        <Source id='polygon-line-source' type='geojson' data={lineString}>
          <Layer
            id='polygon-line'
            type='line'
            paint={{
              'line-color': '#4287f5',
              'line-width': 2,
            }}
          />
        </Source>

        {polygon && (
          <Source id='polygon-fill-source' type='geojson' data={polygon}>
            <Layer
              id='polygon-fill'
              type='fill'
              paint={{
                'fill-color': 'rgba(66, 135, 245, 0.3)',
                'fill-outline-color': '#4287f5',
              }}
            />
          </Source>
        )}
      </>
    );
  };

  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    props.setOpen?.(open, needRefreshData);
  };

  return (
    <Dialog open={props.open} onClose={() => handleSetOpen(false)} fullWidth maxWidth='sm'>
      <DialogTitle className='bg-white text-xl font-semibold text-[#161A1F]'>
        {t('detail')}
        <IconButton className='p-1' onClick={() => handleSetOpen(false)}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent className='border rounded-xl !p-3 mx-7 mb-4 bg-[#F3F6F9]'>
        <BaseRow className='mb-3'>
          <Box className='w-1/3'>
            <Typography className='text-[#7C8796] text-base'>{t('province_ward_id')}:</Typography>
          </Box>
          <Box className='flex-1'>
            <Typography className='text-[#161A1F] text-base'>{`${cast(props.data?.ward_name)} ${cast(props.data?.province_name) ? ', ' + props.data?.province_name : ''}`}</Typography>
          </Box>
        </BaseRow>
        <BaseRow className='mb-3'>
          <Box className='w-1/3'>
            <Typography className='text-[#7C8796] text-base'>{t('name')}:</Typography>
          </Box>
          <Box className='flex-1'>
            <Typography className='text-[#161A1F] text-base'>{cast(props.data?.name)}</Typography>
          </Box>
        </BaseRow>
        <BaseRow className='mb-3'>
          <Box className='w-1/3'>
            <Typography className='text-[#7C8796] text-base'>{t('location_type.title')}:</Typography>
          </Box>
          <Box className='flex-1'>
            <Typography className='text-[#161A1F] text-base'>
              {t('location_type.' + props.data?.location_type)}
            </Typography>
          </Box>
        </BaseRow>
        <BaseRow className='mb-3'>
          <Box className='w-1/3'>
            <Typography className='text-[#7C8796] text-base'>{t('type.title')}:</Typography>
          </Box>
          <Box className='flex-1'>
            <Typography className='text-[#161A1F] text-base'>{t('type.' + props.data?.type)}</Typography>
          </Box>
        </BaseRow>
        <BaseRow className='mb-3'>
          <Box className='w-1/3'>
            <Typography className='text-[#7C8796] text-base'>{t('point')}:</Typography>
          </Box>
          <Box className='flex-1'>
            <Box className={props.data?.points && props.data?.points?.length > 5 ? `overflow-y-scroll h-48` : ''}>
              <Typography className='text-[#161A1F] text-base'>
                {props.data?.points?.map((item, index) => (
                  <Box key={index}>{`{ lat: ${item.lat}, lng: ${item.lng} }`}</Box>
                ))}
              </Typography>
            </Box>
          </Box>
        </BaseRow>
        <BaseRow className='mb-3'>
          <Box className='w-1/3'>
            <Typography className='text-[#7C8796] text-base'>{t('created_by')}:</Typography>
          </Box>
          <Box className='flex-1'>
            <Typography className='text-[#161A1F] text-base'>{props.data?.created_by}</Typography>
          </Box>
        </BaseRow>
        <BaseRow className='mb-3'>
          <Box className='w-1/3'>
            <Typography className='text-[#7C8796] text-base'>{t('created_at')}:</Typography>
          </Box>
          <Box className='flex-1'>
            <Typography className='text-[#161A1F] text-base'>
              {props.data?.created_at ? dayjs(props.data?.created_at).format('DD/MM/YYYY') : ''}
            </Typography>
          </Box>
        </BaseRow>
        <Box>
          <Map
            ref={mapRef}
            onLoad={() => setMapLoaded(true)}
            mapboxAccessToken={process.env.REACT_APP_MAP_BOX_ACCESS_TOKEN}
            initialViewState={{
              longitude: 105.85,
              latitude: 21.02,
              zoom: 6,
            }}
            style={{ width: '100%', height: 400, marginBottom: 20 }}
            mapStyle='mapbox://styles/mapbox/streets-v12'
          >
            {props.data?.points?.map((point: any, index: any) => (
              <Marker key={index} longitude={point.lng} latitude={point.lat}>
                <MarkerIcon />
              </Marker>
            ))}

            {renderPointRadius()}
            {renderPolygon()}
          </Map>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default memo(DetailLocationDialog);
